import { useState } from "react";
import { Link } from "react-router-dom";
import { GAME } from "@/constants/testIds";
import {
  ArrowLeft,
  Crown,
  Trophy,
  Calendar,
  Infinity as InfinityIcon,
} from "lucide-react";
import { todayString } from "@/lib/gameEngine";
import { useLeaderboard } from "@/hooks/useLeaderboard";

export default function LeaderboardScreen() {
  const [mode, setMode] = useState("endless");
  const today = todayString();
  const { rows, loading, error } = useLeaderboard({
    mode,
    seedDate: mode === "daily" ? today : null,
    limit: 10,
  });

  return (
    <div
      data-testid={GAME.leaderboardScreen}
      className="relative min-h-screen w-full bg-black px-4 py-8 sm:py-12 scanlines"
    >
      <div className="max-w-2xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-hud text-white/70 hover:text-white text-base mb-6"
          data-testid="leaderboard-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> BACK TO TITLE
        </Link>

        <div className="text-center mb-6">
          <p className="font-hud text-[var(--street-yellow)] tracking-[0.4em] text-sm">
            // HIGH SCORES
          </p>
          <h1 className="font-display text-6xl sm:text-7xl text-white tracking-tight inline-flex items-center gap-3 mt-1">
            <Trophy className="w-10 h-10 text-[var(--street-yellow)]" />
            LEADERBOARD
          </h1>
        </div>

        <div className="flex items-center justify-center gap-2 mb-6">
          <TabBtn
            active={mode === "endless"}
            onClick={() => setMode("endless")}
            testId="tab-endless"
            icon={<InfinityIcon className="w-4 h-4" />}
            label="ENDLESS"
          />
          <TabBtn
            active={mode === "daily"}
            onClick={() => setMode("daily")}
            testId="tab-daily"
            icon={<Calendar className="w-4 h-4" />}
            label={`DAILY · ${today.slice(5)}`}
          />
        </div>

        <LeaderboardTable
          rows={rows}
          loading={loading}
          error={error}
          mode={mode}
        />

        <div className="mt-8 flex items-center justify-center gap-3">
          <Link
            to="/play"
            data-testid="leaderboard-play-button"
            className="inline-flex items-center gap-2 bg-[var(--neon-red)] hover:bg-red-600 text-white font-body font-bold uppercase tracking-[0.18em] px-5 py-3 rounded-md transition-all"
          >
            ENDLESS RUN
          </Link>
          <Link
            to="/play?mode=daily"
            data-testid="leaderboard-daily-button"
            className="inline-flex items-center gap-2 bg-[var(--street-yellow)] hover:bg-yellow-300 text-black font-body font-bold uppercase tracking-[0.18em] px-5 py-3 rounded-md transition-all"
          >
            <Calendar className="w-4 h-4" /> DAILY RUN
          </Link>
        </div>
      </div>
    </div>
  );
}

function LeaderboardTable({ rows, loading, error, mode }) {
  return (
    <div className="border-2 border-[var(--neon-red)]/50 bg-zinc-950/70 rounded-xl overflow-hidden">
      <div className="grid grid-cols-[60px_1fr_120px_80px] gap-2 px-4 py-3 border-b border-white/10 font-hud text-xs text-white/60 tracking-[0.3em]">
        <span>RANK</span>
        <span>NAME</span>
        <span className="text-right">SCORE</span>
        <span className="text-right">DIST</span>
      </div>

      {loading && (
        <div className="px-4 py-10 text-center font-hud text-[var(--crt-green)]">
          LOADING...
        </div>
      )}
      {!loading && error && (
        <div className="px-4 py-10 text-center font-hud text-[var(--neon-red)]">
          {error}
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="px-4 py-10 text-center font-hud text-white/60">
          {mode === "daily"
            ? "NO DAILY RUNS YET. SET TODAY'S TONE."
            : "NO RUNS YET. BE THE FIRST."}
        </div>
      )}
      {!loading &&
        !error &&
        rows.map((row, i) => <LeaderboardRow key={row.id || `row-${i}`} row={row} rank={i + 1} />)}
    </div>
  );
}

function LeaderboardRow({ row, rank }) {
  const isFirst = rank === 1;
  return (
    <div
      data-testid={GAME.leaderboardRow}
      className={`grid grid-cols-[60px_1fr_120px_80px] gap-2 items-center px-4 py-3 border-b border-white/5 font-hud ${
        isFirst ? "bg-[var(--neon-red)]/10" : ""
      }`}
    >
      <span
        className={`text-xl ${
          isFirst ? "text-[var(--street-yellow)]" : "text-white/80"
        }`}
      >
        {isFirst ? (
          <Crown className="inline w-5 h-5 -mt-1" />
        ) : (
          `#${String(rank).padStart(2, "0")}`
        )}
      </span>
      <span className="text-white text-xl tracking-[0.15em] truncate">
        {row.player_name}
      </span>
      <span className="text-right text-[var(--crt-green)] hud-glow text-xl">
        {String(row.score).padStart(6, "0")}
      </span>
      <span className="text-right text-white/70 text-lg">{row.distance}m</span>
    </div>
  );
}

function TabBtn({ active, onClick, testId, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 font-body font-bold uppercase tracking-[0.18em] text-sm px-4 py-2 rounded-md border-2 transition-all ${
        active
          ? "border-[var(--neon-red)] bg-[var(--neon-red)]/15 text-white"
          : "border-white/20 text-white/60 hover:text-white hover:border-white/50"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
